import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

function CreateTest() {
  const [title, setTitle] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [duration, setDuration] = useState(45);
  const [passingPercentage, setPassingPercentage] = useState(60);
  const [questions, setQuestions] = useState([]);
  const [questionsPerCandidate, setQuestionsPerCandidate] = useState(0);
  const [bulkText, setBulkText] = useState('');
  const [isParsed, setIsParsed] = useState(false);
  const [showAddMore, setShowAddMore] = useState(false);
  const [addMoreText, setAddMoreText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [createdSlug, setCreatedSlug] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();
  const { admin, token, logout, loading: authLoading } = useAuth();

  useEffect(() => {
    if (!authLoading && !admin) {
      navigate('/admin/login');
    }
  }, [admin, authLoading, navigate]);

  const BANK_RATIO = 5;
  const minBankSize = questionsPerCandidate > 0 ? questionsPerCandidate * BANK_RATIO : 0;

  // Smart parser: handles multiple Q&A paste formats
  const parseTextToQuestions = (text) => {
    const lines = text.split('\n').map(l => l.trimEnd());
    const parsed = [];

    // Strategy 1: Detect "Q:" / "A:" or "Question:" / "Answer:" patterns
    const qaRegex = /^(?:Q\d*[\.\)\:\-]?\s*|Question\s*\d*[\.\)\:\-]?\s*)/i;
    const ansRegex = /^(?:A\d*[\.\)\:\-]?\s*|Ans(?:wer)?\s*\d*[\.\)\:\-]?\s*)/i;

    const hasQALabels = lines.some(l => qaRegex.test(l.trim())) && lines.some(l => ansRegex.test(l.trim()));

    if (hasQALabels) {
      let currentQ = null;
      let currentA = null;
      let collectingAnswer = false;

      const flush = () => {
        if (currentQ !== null) {
          parsed.push({
            question: currentQ.trim(),
            expectedAnswer: (currentA || '').trim(),
            points: 1
          });
        }
        currentQ = null;
        currentA = null;
        collectingAnswer = false;
      };

      for (const line of lines) {
        const trimmed = line.trim();
        if (qaRegex.test(trimmed)) {
          flush();
          currentQ = trimmed.replace(qaRegex, '').trim();
          collectingAnswer = false;
        } else if (ansRegex.test(trimmed)) {
          currentA = trimmed.replace(ansRegex, '').trim();
          collectingAnswer = true;
        } else if (trimmed !== '') {
          if (collectingAnswer) {
            currentA = (currentA || '') + ' ' + trimmed;
          } else if (currentQ !== null) {
            currentQ += ' ' + trimmed;
          }
        }
      }
      flush();
    } else {
      // Strategy 2: Detect numbered patterns like "1." or "1)" as questions
      // with answer on next line(s) until next number
      const numberedRegex = /^(\d+)\s*[\.\)\:\-]\s*/;
      const hasNumbered = lines.filter(l => numberedRegex.test(l.trim())).length >= 2;

      if (hasNumbered) {
        // Collect blocks: each numbered line starts a new block
        // If blocks alternate evenly, treat odd blocks as Q, even as A
        // Otherwise treat each numbered line as Q, following non-numbered lines as A
        const blocks = [];
        let currentBlock = null;

        for (const line of lines) {
          const trimmed = line.trim();
          if (numberedRegex.test(trimmed)) {
            if (currentBlock) blocks.push(currentBlock);
            currentBlock = { first: trimmed.replace(numberedRegex, '').trim(), rest: [] };
          } else if (trimmed !== '' && currentBlock) {
            currentBlock.rest.push(trimmed);
          }
        }
        if (currentBlock) blocks.push(currentBlock);

        // Check if any block's rest looks like an answer (has content after the question line)
        const blocksWithRest = blocks.filter(b => b.rest.length > 0);

        if (blocksWithRest.length >= blocks.length * 0.5) {
          // Each block is Q (first line) + A (rest lines)
          for (const block of blocks) {
            parsed.push({
              question: block.first,
              expectedAnswer: block.rest.join(' ').trim(),
              points: 1
            });
          }
        } else {
          // Pair blocks: 1st=Q, 2nd=A, 3rd=Q, 4th=A...
          for (let i = 0; i < blocks.length - 1; i += 2) {
            parsed.push({
              question: [blocks[i].first, ...blocks[i].rest].join(' ').trim(),
              expectedAnswer: [blocks[i + 1].first, ...blocks[i + 1].rest].join(' ').trim(),
              points: 1
            });
          }
        }
      } else {
        // Strategy 3: Fallback — treat every two non-empty lines as Q then A
        const nonEmpty = lines.filter(l => l.trim() !== '');
        for (let i = 0; i < nonEmpty.length - 1; i += 2) {
          parsed.push({
            question: nonEmpty[i].trim(),
            expectedAnswer: nonEmpty[i + 1].trim(),
            points: 1
          });
        }
      }
    }

    return parsed;
  };

  const parseBulkText = () => {
    const text = bulkText.trim();
    if (!text) {
      setError('Paste your questions and answers first');
      return;
    }
    setError('');
    const parsed = parseTextToQuestions(text);
    if (parsed.length === 0) {
      setError('Could not parse any Q&A pairs. Use formats like:\n• Q: ... / A: ...\n• 1. Question\\n   Answer\n• Alternating lines (question then answer)');
      return;
    }
    setQuestions(parsed);
    setIsParsed(true);
  };

  const handleAddMore = () => {
    const text = addMoreText.trim();
    if (!text) {
      setError('Paste additional questions and answers first');
      return;
    }
    setError('');
    const parsed = parseTextToQuestions(text);
    if (parsed.length === 0) {
      setError('Could not parse any Q&A pairs from the additional text.');
      return;
    }
    setQuestions(prev => [...prev, ...parsed]);
    setAddMoreText('');
    setShowAddMore(false);
  };

  const resetParse = () => {
    setIsParsed(false);
    setQuestions([]);
    setShowAddMore(false);
    setAddMoreText('');
  };

  const removeQuestion = (index) => {
    setQuestions(questions.filter((_, i) => i !== index));
  };

  const updateQuestion = (index, field, value) => {
    const updated = [...questions];
    updated[index] = { ...updated[index], [field]: value };
    setQuestions(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validation
    if (!title.trim()) {
      setError('Test title is required');
      setLoading(false);
      return;
    }
    if (questions.some(q => !q.question.trim())) {
      setError('All questions must have text');
      setLoading(false);
      return;
    }
    if (questions.some(q => !q.expectedAnswer.trim())) {
      setError('All questions must have expected answers');
      setLoading(false);
      return;
    }

    if (questionsPerCandidate > 0 && questions.length < questionsPerCandidate * BANK_RATIO) {
      setError(`Randomization requires at least ${questionsPerCandidate * BANK_RATIO} questions in the bank (${BANK_RATIO}x ratio). You have ${questions.length}.`);
      setLoading(false);
      return;
    }

    if (questionsPerCandidate > 0 && questionsPerCandidate >= questions.length) {
      setError('Questions per candidate must be less than total questions in the bank.');
      setLoading(false);
      return;
    }

    try {
      const res = await axios.post('/api/tests', {
        title,
        jobDescription,
        questions,
        duration: Number(duration),
        passingPercentage: Number(passingPercentage),
        questionsPerCandidate: Number(questionsPerCandidate) || 0
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const slug = res.data.test?.shareableSlug || res.data.shareableSlug;
      setCreatedSlug(slug);
      setShowModal(true);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to create test');
    } finally {
      setLoading(false);
    }
  };

  const copyLink = async () => {
    const link = `${window.location.origin}/exam/${createdSlug}`;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = link;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (authLoading) {
    return (
      <div className="admin-layout">
        <aside className="sidebar">
          <div className="sidebar-header"><h2>Exam System</h2></div>
        </aside>
        <main className="admin-main">
          <div className="loading-spinner">Loading...</div>
        </main>
      </div>
    );
  }

  return (
    <div className="admin-layout">
      <aside className="sidebar">
        <div className="sidebar-header">
          <h2>Exam System</h2>
        </div>
        <nav className="sidebar-nav">
          <Link to="/admin/dashboard" className="nav-link">Dashboard</Link>
          <Link to="/admin/create-test" className="nav-link active">Create Test</Link>
          <Link to="/admin/candidates" className="nav-link">Candidates</Link>
        </nav>
        <div className="sidebar-footer">
          <div className="admin-info">
            <span className="admin-name">{admin?.name || admin?.email}</span>
          </div>
          <button className="btn btn-logout" onClick={logout}>Logout</button>
        </div>
      </aside>

      <main className="admin-main">
        <div className="admin-header">
          <h1>Create New Test</h1>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit} className="create-test-form">
          <div className="create-test-layout">
            {/* Left Section: Job Description */}
            <div className="create-test-left">
              <div className="form-card">
                <h2 className="form-card-title">Test Details</h2>
                <div className="form-group">
                  <label htmlFor="title">Test Title</label>
                  <input
                    id="title"
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g., Senior React Developer Screening"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="jobDescription">Job Description</label>
                  <textarea
                    id="jobDescription"
                    value={jobDescription}
                    onChange={(e) => setJobDescription(e.target.value)}
                    placeholder="Enter the full job description here..."
                    rows={10}
                    className="textarea-large"
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="duration">Duration (minutes)</label>
                    <input
                      id="duration"
                      type="number"
                      value={duration}
                      onChange={(e) => setDuration(e.target.value)}
                      min={1}
                      max={300}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="passingPercentage">Passing Percentage (%)</label>
                    <input
                      id="passingPercentage"
                      type="number"
                      value={passingPercentage}
                      onChange={(e) => setPassingPercentage(e.target.value)}
                      min={1}
                      max={100}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="questionsPerCandidate">Questions Per Candidate (0 = all, no randomization)</label>
                  <input
                    id="questionsPerCandidate"
                    type="number"
                    value={questionsPerCandidate}
                    onChange={(e) => setQuestionsPerCandidate(e.target.value)}
                    min={0}
                    max={1000}
                  />
                  {questionsPerCandidate > 0 && (
                    <p className="form-hint">
                      Minimum bank size: {questionsPerCandidate * BANK_RATIO} questions (5x ratio).
                      {questions.length > 0 && (
                        <> You have <strong>{questions.length}</strong> — {questions.length >= questionsPerCandidate * BANK_RATIO ? <span style={{color: '#27ae60'}}>ready</span> : <span style={{color: '#e74c3c'}}>need {questionsPerCandidate * BANK_RATIO - questions.length} more</span>}.</>
                      )}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Right Section: Bulk Paste Q&A */}
            <div className="create-test-right">
              <div className="form-card">
                {!isParsed ? (
                  <>
                    <div className="form-card-header">
                      <h2 className="form-card-title">Paste Questions &amp; Answers</h2>
                    </div>
                    <div className="form-group">
                      <label htmlFor="bulkText">Paste all your questions and answers below</label>
                      <textarea
                        id="bulkText"
                        value={bulkText}
                        onChange={(e) => setBulkText(e.target.value)}
                        placeholder={`Paste in any of these formats:\n\nFormat 1 — Q/A labels:\nQ: What is Kubernetes?\nA: An open-source container orchestration platform...\n\nQ: Explain Docker networking\nA: Docker networking allows containers...\n\nFormat 2 — Numbered with answers:\n1. What is Kubernetes?\nAn open-source container orchestration platform...\n\n2. Explain Docker networking\nDocker networking allows containers...\n\nFormat 3 — Alternating lines:\nWhat is Kubernetes?\nAn open-source container orchestration platform...\nExplain Docker networking\nDocker networking allows containers...`}
                        rows={20}
                        className="textarea-large textarea-mono"
                      />
                    </div>
                    <button type="button" className="btn btn-primary btn-full" onClick={parseBulkText}>
                      Parse Questions ({bulkText.trim() ? bulkText.trim().split('\n').filter(l => l.trim()).length : 0} lines)
                    </button>
                  </>
                ) : (
                  <>
                    <div className="form-card-header">
                      <h2 className="form-card-title">Parsed: {questions.length} Questions</h2>
                      <button type="button" className="btn btn-outline" onClick={resetParse}>
                        Back to Paste
                      </button>
                    </div>

                    <div className="questions-list">
                      {questions.map((q, index) => (
                        <div key={index} className="question-item">
                          <div className="question-header">
                            <span className="question-number">Q{index + 1}</span>
                            <div className="question-actions">
                              <div className="form-group form-group-inline">
                                <label>Points:</label>
                                <input
                                  type="number"
                                  value={q.points}
                                  onChange={(e) => updateQuestion(index, 'points', Number(e.target.value))}
                                  min={1}
                                  max={100}
                                  className="input-sm"
                                />
                              </div>
                              <button
                                type="button"
                                className="btn btn-danger btn-sm"
                                onClick={() => removeQuestion(index)}
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                          <div className="form-group">
                            <label>Question</label>
                            <textarea
                              value={q.question}
                              onChange={(e) => updateQuestion(index, 'question', e.target.value)}
                              rows={2}
                            />
                          </div>
                          <div className="form-group">
                            <label>Expected Answer</label>
                            <textarea
                              value={q.expectedAnswer}
                              onChange={(e) => updateQuestion(index, 'expectedAnswer', e.target.value)}
                              rows={2}
                            />
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Add More Questions */}
                    {!showAddMore ? (
                      <button
                        type="button"
                        className="btn btn-outline btn-full"
                        onClick={() => setShowAddMore(true)}
                        style={{ marginTop: '1rem' }}
                      >
                        + Add More Questions
                      </button>
                    ) : (
                      <div className="add-more-section" style={{ marginTop: '1rem' }}>
                        <h3 className="form-card-title">Paste Additional Questions</h3>
                        <div className="form-group">
                          <textarea
                            value={addMoreText}
                            onChange={(e) => setAddMoreText(e.target.value)}
                            placeholder="Paste more Q&A pairs here using the same formats..."
                            rows={10}
                            className="textarea-large textarea-mono"
                          />
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button type="button" className="btn btn-primary" onClick={handleAddMore}>
                            Parse & Add ({addMoreText.trim() ? addMoreText.trim().split('\n').filter(l => l.trim()).length : 0} lines)
                          </button>
                          <button type="button" className="btn btn-outline" onClick={() => { setShowAddMore(false); setAddMoreText(''); }}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn btn-primary btn-lg" disabled={loading}>
              {loading ? 'Creating Test...' : 'Create Test'}
            </button>
          </div>
        </form>

        {/* Success Modal */}
        {showModal && (
          <div className="modal-overlay" onClick={() => setShowModal(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Test Created Successfully!</h2>
              </div>
              <div className="modal-body">
                <p>Share this link with candidates:</p>
                <div className="link-box">
                  <code>{window.location.origin}/exam/{createdSlug}</code>
                  <button className="btn btn-primary btn-sm" onClick={copyLink}>
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline" onClick={() => {
                  setShowModal(false);
                  navigate('/admin/dashboard');
                }}>
                  Go to Dashboard
                </button>
                <button className="btn btn-primary" onClick={() => {
                  setShowModal(false);
                  setTitle('');
                  setJobDescription('');
                  setDuration(45);
                  setPassingPercentage(60);
                  setQuestionsPerCandidate(0);
                  setQuestions([]);
                  setBulkText('');
                  setIsParsed(false);
                  setShowAddMore(false);
                  setAddMoreText('');
                  setCreatedSlug('');
                }}>
                  Create Another Test
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default CreateTest;
